import {
  ACHIEVEMENTS,
  AFFIXES,
  BOSSES,
  CLASSES,
  COMPANIONS,
  ELITE_AFFIXES,
  ENEMIES,
  FIXED_STEP,
  ITEM_BASES,
  QUESTS,
  RARITIES,
  REGIONS,
  SAVE_SCHEMA_VERSION,
  SKILL_TREES,
  STATUS_DEFINITIONS,
  WORLD_SIZE,
} from "./config.js";

export const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
export const distance = (a, b) => Math.hypot(a.x - b.x, a.z - b.z);
export const clone = (value) => JSON.parse(JSON.stringify(value));
const finite = (value, fallback = 0) =>
  Number.isFinite(value) ? value : fallback;

function segmentDistanceSquared(start, end, point) {
  const dx = end.x - start.x;
  const dz = end.z - start.z;
  const lengthSquared = dx * dx + dz * dz;
  const t =
    lengthSquared > 0
      ? clamp(
          ((point.x - start.x) * dx + (point.z - start.z) * dz) / lengthSquared,
          0,
          1,
        )
      : 0;
  const x = start.x + dx * t;
  const z = start.z + dz * t;
  return (point.x - x) ** 2 + (point.z - z) ** 2;
}

export function safeMove(position, delta, canOccupy = () => true) {
  const steps = Math.max(1, Math.ceil(Math.hypot(delta.x, delta.z) / 0.22));
  let current = { x: position.x, z: position.z };
  for (let step = 0; step < steps; step += 1) {
    const movement = { x: delta.x / steps, z: delta.z / steps };
    const combined = { x: current.x + movement.x, z: current.z + movement.z };
    if (canOccupy(combined)) {
      current = combined;
      continue;
    }
    const alongX = { x: current.x + movement.x, z: current.z };
    const alongZ = { x: current.x, z: current.z + movement.z };
    if (canOccupy(alongX)) current = alongX;
    else if (canOccupy(alongZ)) current = alongZ;
    else break;
  }
  return current;
}

export class SeededRandom {
  constructor(seed = 1) {
    const text = String(seed);
    let hash = 2166136261;
    for (let index = 0; index < text.length; index += 1) {
      hash ^= text.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }
    this.state = hash >>> 0 || 0x6d2b79f5;
  }
  next() {
    let value = (this.state += 0x6d2b79f5);
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  }
  int(min, max) {
    return Math.floor(this.next() * (max - min + 1)) + min;
  }
  pick(values) {
    return values[Math.floor(this.next() * values.length)];
  }
  shuffle(values) {
    const result = values.slice();
    for (let index = result.length - 1; index > 0; index -= 1) {
      const swap = this.int(0, index);
      [result[index], result[swap]] = [result[swap], result[index]];
    }
    return result;
  }
}

export class IdFactory {
  constructor(prefix = "rf", counter = 0) {
    this.prefix = prefix;
    this.counter = counter;
  }
  next(kind = "entity") {
    this.counter += 1;
    return `${this.prefix}-${kind}-${this.counter.toString(36)}`;
  }
}

export function validateCharacterName(raw) {
  const name = String(raw ?? "")
    .trim()
    .replace(/\s+/g, " ");
  if (name.length < 2 || name.length > 20)
    return { valid: false, name, error: "Use 2–20 characters." };
  if (/[\u0000-\u001f\u007f-\u009f]/u.test(name))
    return { valid: false, name, error: "Control characters are not allowed." };
  if (!/[\p{L}\p{N}]/u.test(name))
    return {
      valid: false,
      name,
      error: "Include at least one letter or number.",
    };
  return { valid: true, name, error: "" };
}

export function defaultStatistics() {
  return {
    playTime: 0,
    charactersCreated: 0,
    enemiesDefeated: 0,
    elitesDefeated: 0,
    minibossesDefeated: 0,
    bossesDefeated: 0,
    damageDealt: 0,
    damageReceived: 0,
    healing: 0,
    criticalHits: 0,
    dodges: 0,
    blocks: 0,
    parries: 0,
    deaths: 0,
    echoCachesRecovered: 0,
    questsCompleted: 0,
    sideQuestsCompleted: 0,
    dungeonsCompleted: 0,
    highestChallengeTier: 0,
    itemsCollected: 0,
    legendaryItems: 0,
    mythicItems: 0,
    goldEarned: 0,
    goldSpent: 0,
    craftingOperations: 0,
    itemsSalvaged: 0,
    skillRespecs: 0,
    companionsRecruited: 0,
    regionsDiscovered: 1,
    secretsFound: 0,
    highestDamageHit: 0,
    fastestBossDefeat: null,
    abilityUse: {},
    campaignEndings: [],
  };
}

export function derivedStats(character) {
  const klass = CLASSES[character.classId] ?? CLASSES.barbarian;
  const result = { ...klass.base };
  result.health +=
    Math.max(0, character.level - 1) * 8 +
    (character.attributes?.vitality ?? 0) * 6;
  result.power +=
    Math.max(0, character.level - 1) * 1.4 +
    (character.attributes?.power ?? 0) * 1.6;
  result.armor += (character.attributes?.guard ?? 0) * 2;
  for (const item of Object.values(character.equipment ?? {})) {
    if (!item) continue;
    result.power += item.power ?? 0;
    result.armor += item.armor ?? 0;
    for (const affix of item.affixes ?? [])
      result[affix.stat] = (result[affix.stat] ?? 0) + affix.value;
  }
  for (const node of SKILL_TREES[character.classId] ?? []) {
    const rank = character.skills?.[node.id] ?? 0;
    if (node.id.endsWith("-vigor")) result.health += rank * 8;
    if (node.id.endsWith("-ward")) result.armor += rank * 5;
    if (node.id.endsWith("-flow"))
      result.resourceGeneration =
        (result.resourceGeneration ?? 0) + rank * 0.07;
  }
  return result;
}

export function createCharacter(
  input,
  idFactory = new IdFactory("character"),
  now = Date.now(),
) {
  const nameCheck = validateCharacterName(input.name);
  if (!nameCheck.valid) throw new Error(nameCheck.error);
  if (!CLASSES[input.classId]) throw new Error("Unknown class.");
  const character = {
    id: idFactory.next("warden"),
    name: nameCheck.name,
    classId: input.classId,
    level: 1,
    xp: 0,
    createdAt: now,
    updatedAt: now,
    playTime: 0,
    mode: input.mode ?? "campaign",
    appearance: {
      body: String(input.body || "Stalwart"),
      skin: /^#[0-9a-f]{6}$/i.test(input.skin) ? input.skin : "#d9a978",
      hair: String(input.hair || "Windswept"),
      hairColor: /^#[0-9a-f]{6}$/i.test(input.hairColor)
        ? input.hairColor
        : "#402d25",
      accent: /^#[0-9a-f]{6}$/i.test(input.accent)
        ? input.accent
        : CLASSES[input.classId].color,
      tone: String(input.tone || "Resolute"),
      helmet: Boolean(input.helmet),
      cape: Boolean(input.cape),
    },
    attributes: { power: 0, vitality: 0, guard: 0 },
    attributePoints: 0,
    skillPoints: 1,
    skills: {},
    inventory: [],
    equipment: {
      weapon: null,
      offHand: null,
      helmet: null,
      armor: null,
      gloves: null,
      boots: null,
      necklace: null,
      ringOne: null,
      ringTwo: null,
    },
    stash: [],
    capacity: 30,
    currencies: { gold: 125, veilShards: 0, challengeMarks: 0 },
    potions: 3,
    potionCapacity: 3,
    quests: {
      "tutorial-awakening": {
        state: input.mode === "tutorial" ? "active" : "available",
        objective: 0,
        progress: 0,
      },
      "chapter-1": {
        state: input.mode === "campaign" ? "active" : "available",
        objective: 0,
        progress: 0,
      },
    },
    chapter: input.mode === "tutorial" ? 0 : 1,
    campaignChoices: {},
    currentZone: input.mode === "tutorial" ? "veilwatch" : "verdant-march",
    discoveredRegions: [
      "veilwatch",
      input.mode === "tutorial" ? "veilwatch" : "verdant-march",
    ],
    checkpoints: { veilwatch: { x: 0, z: 7 } },
    position:
      input.mode === "tutorial" ? { x: 0, y: 0, z: 6 } : { x: 0, y: 0, z: 3 },
    activeCompanion: null,
    recruitedCompanions: [],
    echoCache: null,
    challengeTier: 1,
    statistics: defaultStatistics(),
    achievements: ["first-warden"],
    bestiary: {},
    itemLibrary: [],
    endings: [],
  };
  const starter = generateItem(
    input.classId === "arcanist"
      ? "aster-staff"
      : input.classId === "rogue"
        ? "shade-knives"
        : input.classId === "ranger"
          ? "march-bow"
          : input.classId === "paladin"
            ? "sun-mace"
            : input.classId === "necromancer"
              ? "bone-wand"
              : "rift-axe",
    1,
    "common",
    new SeededRandom(character.id),
    idFactory,
  );
  starter.owner = "equipped";
  character.equipment.weapon = starter;
  character.statistics.charactersCreated = 1;
  return character;
}

function rarityFromRoll(roll) {
  let cursor = 0;
  const total = Object.values(RARITIES).reduce(
    (sum, entry) => sum + entry.weight,
    0,
  );
  for (const [id, entry] of Object.entries(RARITIES)) {
    cursor += entry.weight / total;
    if (roll <= cursor) return id;
  }
  return "common";
}

export function generateItem(
  baseId,
  level = 1,
  rarityId = null,
  rng = new SeededRandom(),
  idFactory = new IdFactory("loot"),
) {
  const base =
    ITEM_BASES.find((entry) => entry.id === baseId) ?? rng.pick(ITEM_BASES);
  const rarity = RARITIES[rarityId] ? rarityId : rarityFromRoll(rng.next());
  const compatible = AFFIXES.filter((entry) => entry.types.includes(base.type));
  const affixes = [];
  for (const definition of rng
    .shuffle(compatible)
    .slice(0, RARITIES[rarity].affixes)) {
    const raw = definition.min + (definition.max - definition.min) * rng.next();
    affixes.push({
      id: definition.id,
      name: definition.name,
      stat: definition.stat,
      value: Number(raw.toFixed(raw < 1 ? 3 : 1)),
    });
  }
  return {
    ...clone(base),
    id: idFactory.next("item"),
    definitionId: base.id,
    level: clamp(Math.floor(level), 1, 100),
    rarity,
    name:
      rarity === "common"
        ? base.name
        : `${rng.pick(["Veiled", "Starforged", "Oathbound", "Briar", "Echoing"])} ${base.name}`,
    affixes,
    owner: "ground",
    value: Math.round((12 + level * 6) * (1 + RARITIES[rarity].affixes * 0.65)),
  };
}

export function itemSlot(item, equipment) {
  if (item.slot !== "ring") return item.slot;
  return equipment.ringOne ? "ringTwo" : "ringOne";
}

export function equipItem(character, itemId) {
  const index = character.inventory.findIndex((item) => item.id === itemId);
  if (index < 0) return { ok: false, reason: "Item is not in inventory." };
  const item = character.inventory[index];
  const slot = itemSlot(item, character.equipment);
  if (!Object.hasOwn(character.equipment, slot))
    return { ok: false, reason: "Invalid equipment slot." };
  character.inventory.splice(index, 1);
  const displaced = character.equipment[slot];
  if (displaced) {
    displaced.owner = "inventory";
    character.inventory.push(displaced);
  }
  item.owner = "equipped";
  character.equipment[slot] = item;
  return { ok: true, slot, displaced };
}

export function unequipItem(character, slot) {
  if (!Object.hasOwn(character.equipment, slot))
    return { ok: false, reason: "Invalid equipment slot." };
  const item = character.equipment[slot];
  if (!item) return { ok: false, reason: "Equipment slot is empty." };
  if (character.inventory.length >= character.capacity)
    return { ok: false, reason: "Inventory full." };
  character.equipment[slot] = null;
  item.owner = "inventory";
  character.inventory.push(item);
  return { ok: true, item };
}

export function transferItem(character, itemId, from, to) {
  if (
    ![
      ["inventory", "stash"],
      ["stash", "inventory"],
    ].some(([source, destination]) => source === from && destination === to)
  )
    return { ok: false, reason: "Unsupported item transfer." };
  const source = character[from];
  const destination = character[to];
  const index = source.findIndex((item) => item.id === itemId);
  if (index < 0) return { ok: false, reason: "Item is no longer available." };
  if (to === "inventory" && destination.length >= character.capacity)
    return { ok: false, reason: "Inventory full." };
  const [item] = source.splice(index, 1);
  item.owner = to;
  destination.push(item);
  return { ok: true, item };
}

export function buyVendorItem(character, stock, itemId) {
  const index = stock.findIndex((item) => item.id === itemId && !item.sold);
  if (index < 0) return { ok: false, reason: "Item is no longer in stock." };
  const item = stock[index];
  if (character.inventory.length >= character.capacity)
    return { ok: false, reason: "Inventory full. Purchase cancelled." };
  if (character.currencies.gold < item.value)
    return { ok: false, reason: "Insufficient Gold." };
  character.currencies.gold -= item.value;
  character.statistics.goldSpent += item.value;
  stock.splice(index, 1);
  item.owner = "inventory";
  character.inventory.push(item);
  return { ok: true, item };
}

export function sellInventoryItem(character, itemId) {
  const index = character.inventory.findIndex((item) => item.id === itemId);
  if (index < 0) return { ok: false, reason: "Item is no longer available." };
  const [item] = character.inventory.splice(index, 1);
  const gold = Math.max(1, Math.floor(item.value * 0.45));
  item.owner = "vendor";
  character.currencies.gold += gold;
  character.statistics.goldEarned += gold;
  return { ok: true, item, gold };
}

export function salvageItem(character, itemId) {
  const index = character.inventory.findIndex((item) => item.id === itemId);
  if (index < 0) return { ok: false, reason: "Item is not available." };
  const [item] = character.inventory.splice(index, 1);
  item.owner = "destroyed";
  const shards = Math.max(1, (RARITIES[item.rarity]?.affixes ?? 0) + 1);
  character.currencies.veilShards += shards;
  character.statistics.itemsSalvaged += 1;
  return { ok: true, shards, item };
}

export function validateItemOwnership(
  character,
  groundItems = [],
  vendorStocks = {},
) {
  const locations = new Map();
  const add = (item, owner) => {
    if (!item?.id) return;
    const current = locations.get(item.id) ?? [];
    current.push(owner);
    locations.set(item.id, current);
  };
  character.inventory.forEach((item) => add(item, "inventory"));
  character.stash.forEach((item) => add(item, "stash"));
  Object.values(character.equipment).forEach((item) => add(item, "equipped"));
  groundItems.forEach((item) => add(item, "ground"));
  const vendorItems = Object.values(vendorStocks ?? {}).flat();
  vendorItems.forEach((item) => add(item, "vendor"));
  if (character.echoCache?.items)
    character.echoCache.items.forEach((item) => add(item, "corpse-cache"));
  const duplicates = [...locations.entries()].filter(
    ([, owners]) => owners.length !== 1,
  );
  const mismatches = [...locations.entries()].filter(([id, owners]) => {
    const item = [
      ...character.inventory,
      ...character.stash,
      ...Object.values(character.equipment).filter(Boolean),
      ...groundItems,
      ...vendorItems,
    ].find((entry) => entry?.id === id);
    return item && item.owner !== owners[0];
  });
  return {
    valid: duplicates.length === 0 && mismatches.length === 0,
    duplicates,
    mismatches,
    count: locations.size,
  };
}

export function resolveDamage(input, rng = new SeededRandom()) {
  if (
    !input.targetAlive ||
    !Number.isFinite(input.baseDamage) ||
    input.baseDamage < 0
  )
    return {
      valid: false,
      healthDamage: 0,
      reason: "Invalid target or damage.",
    };
  let value = Math.max(0, input.baseDamage); // 1. base damage
  value *= Math.max(0, finite(input.attackerModifier, 1)); // 2. attacker modifiers
  value *= Math.max(0, finite(input.abilityModifier, 1)); // 3. ability modifiers
  value *= Math.max(0, finite(input.elementalModifier, 1)); // 4. elemental modifiers
  const critical = rng.next() < clamp(finite(input.criticalChance), 0, 0.85); // 5. critical calculation
  if (critical) value *= Math.max(1, finite(input.criticalMultiplier, 1.5));
  const mitigation =
    input.damageType === "physical"
      ? clamp(finite(input.armor) / (finite(input.armor) + 100), 0, 0.75)
      : clamp(finite(input.resistance), 0, 0.75); // 6. armor/resistance
  value *= 1 - mitigation;
  if (input.weak) value *= 0.8; // 7. weak/vulnerable
  if (input.vulnerable) value *= 1.2;
  const parried = Boolean(input.parry); // 8. block/parry
  const blocked = !parried && Boolean(input.block);
  if (parried) value = 0;
  else if (blocked) value *= 0.45;
  value = finite(value);
  const shieldAbsorbed = Math.min(Math.max(0, finite(input.shield)), value); // 9. shield absorption
  const healthDamage = Math.max(0, Number((value - shieldAbsorbed).toFixed(2))); // 10. health damage
  return {
    valid: true,
    critical,
    blocked,
    parried,
    shieldAbsorbed,
    healthDamage,
    status: input.status ?? null,
  }; // 11–12 status then death by caller
}

export function addStatus(entity, statusId, sourceId, now, options = {}) {
  const definition = STATUS_DEFINITIONS[statusId];
  if (!definition || entity.immunities?.includes(statusId)) return false;
  entity.statuses ??= [];
  const current = entity.statuses.find((status) => status.id === statusId);
  if (current) {
    if (definition.policy === "stack")
      current.stacks = Math.min(definition.maxStacks, current.stacks + 1);
    if (
      definition.policy !== "replace" ||
      (options.duration ?? definition.duration) >= current.duration
    ) {
      current.remaining = options.duration ?? definition.duration;
      current.sourceId = sourceId;
    }
    return true;
  }
  entity.statuses.push({
    id: statusId,
    sourceId,
    stacks: 1,
    remaining: options.duration ?? definition.duration,
    tickRemaining: definition.interval,
    amount: options.amount ?? 3,
    duration: options.duration ?? definition.duration,
  });
  if (statusId === "shield")
    entity.shield = Math.max(entity.shield ?? 0, options.amount ?? 25);
  return true;
}

export function updateStatuses(entity, dt, onTick = () => {}) {
  const expired = [];
  for (const status of entity.statuses ?? []) {
    const definition = STATUS_DEFINITIONS[status.id];
    status.remaining -= dt;
    if (definition?.interval) {
      status.tickRemaining -= dt;
      while (status.tickRemaining <= 0 && status.remaining > 0) {
        status.tickRemaining += definition.interval;
        onTick(status, definition);
      }
    }
    if (status.remaining <= 0) expired.push(status.id);
  }
  entity.statuses = (entity.statuses ?? []).filter(
    (status) => status.remaining > 0,
  );
  if (expired.includes("shield")) entity.shield = 0;
}

export function hasStatus(entity, id) {
  return Boolean(entity.statuses?.some((status) => status.id === id));
}

export function createRuntime(character, seed = Date.now()) {
  const stats = derivedStats(character);
  const klass = CLASSES[character.classId];
  return {
    schemaVersion: SAVE_SCHEMA_VERSION,
    generationToken: `realm-${String(seed)}`,
    tokens: {
      session: `session-${String(seed)}`,
      profile: character.id,
      zone: 1,
      dungeon: 1,
      combat: 1,
      playerLife: 1,
      bossPhase: 1,
      restore: 1,
      assetLoad: 1,
      pathRequest: 1,
    },
    seed: String(seed),
    time: 0,
    paused: true,
    gameState: "paused",
    fixedStep: FIXED_STEP,
    accumulator: 0,
    actionCounter: 0,
    mode: character.mode,
    zoneId: character.currentZone,
    chapter: character.chapter,
    character,
    player: {
      id: character.id,
      position: { ...character.position },
      velocity: { x: 0, z: 0 },
      facing: { x: 0, z: 1 },
      health: stats.health,
      maxHealth: stats.health,
      resource: klass.id === "barbarian" ? 0 : klass.base.resource,
      maxResource: klass.base.resource,
      statuses: [],
      cooldowns: {},
      dodgeRemaining: 0,
      invulnerable: 0,
      blocking: false,
      parryRemaining: 0,
      attackRemaining: 0,
      animation: "idle",
      alive: true,
      path: [],
      targetId: null,
      potions: character.potions,
    },
    enemies: [],
    projectiles: [],
    effects: [],
    groundItems: [],
    corpses: [],
    interactables: [],
    companion: null,
    summons: [],
    checkpoint: character.checkpoints[character.currentZone] ?? {
      x: character.position.x,
      z: character.position.z,
    },
    boss: null,
    worldRevision: 1,
    questEvents: [],
    pendingAnnouncements: [],
    defeatedBosses: character.defeatedBosses ?? [],
    processedEvents: [],
    diagnostics: { physicsMs: 0, aiMs: 0, pathMs: 0 },
  };
}

export function spawnEnemy(
  runtime,
  definitionId,
  position,
  options = {},
  idFactory = new IdFactory(
    runtime.generationToken,
    runtime.idCounter ?? runtime.actionCounter,
  ),
) {
  const definition =
    ENEMIES.find((entry) => entry.id === definitionId) ?? ENEMIES[0];
  const levelScale = 1 + Math.max(0, runtime.character.level - 1) * 0.12;
  const elite = options.elite ? (options.affix ?? ELITE_AFFIXES[0]) : null;
  const health = Math.round(
    definition.health *
      levelScale *
      (elite?.health ?? 1) *
      (options.boss ? 10 : options.miniboss ? 4 : 1),
  );
  const entity = {
    id: idFactory.next(options.boss ? "boss" : "enemy"),
    definitionId: definition.id,
    name: options.name ?? definition.name,
    faction: definition.faction,
    role: definition.role,
    position: { x: position.x, y: 0, z: position.z },
    spawnPosition: { x: position.x, z: position.z },
    velocity: { x: 0, z: 0 },
    facing: { x: 0, z: -1 },
    health,
    maxHealth: health,
    damage: definition.damage * levelScale * (options.boss ? 2 : 1),
    armor: definition.armor ?? 5,
    speed: definition.speed * (elite?.speed ?? 1),
    range: definition.range,
    statuses: [],
    alive: true,
    state: "idle",
    thinkRemaining: 0.15,
    attackCooldown: 0.5,
    staggerRemaining: 0,
    targetId: null,
    elite: elite
      ? { id: elite.id, name: elite.name, color: elite.color }
      : null,
    miniboss: Boolean(options.miniboss),
    bossId: options.bossId ?? null,
    challengeBoss: Boolean(options.challenge),
    summonedBy: options.summonedBy ?? null,
    phase: 1,
    phaseTransitions: [],
    zoneId: runtime.zoneId,
    generationToken: runtime.tokens?.zone ?? 1,
    receivedAttackIds: [],
    deathRemaining: 0,
    lootSeed: `${runtime.seed}:${runtime.actionCounter}:${definition.id}`,
  };
  runtime.enemies.push(entity);
  if (options.boss) runtime.boss = entity;
  runtime.idCounter = Math.max(runtime.idCounter ?? 0, idFactory.counter);
  return entity;
}

export function spawnZonePopulation(
  runtime,
  rng = new SeededRandom(runtime.seed),
  idFactory = new IdFactory(
    runtime.generationToken,
    runtime.idCounter ?? runtime.actionCounter,
  ),
) {
  const region =
    REGIONS.find((entry) => entry.id === runtime.zoneId) ?? REGIONS[1];
  if (region.id === "veilwatch") return [];
  const available = ENEMIES.filter((entry) =>
    region.factions.includes(entry.faction),
  );
  const count = clamp(7 + runtime.chapter * 2, 8, 18);
  for (let index = 0; index < count; index += 1) {
    const angle = rng.next() * Math.PI * 2;
    const radius = 10 + rng.next() * 18;
    spawnEnemy(
      runtime,
      rng.pick(available).id,
      { x: Math.cos(angle) * radius, z: Math.sin(angle) * radius },
      { elite: index > 3 && rng.next() < 0.16, affix: rng.pick(ELITE_AFFIXES) },
      idFactory,
    );
  }
  runtime.interactables.push({
    id: idFactory.next("waystone"),
    type: "checkpoint",
    name: "Veil Waystone",
    position: { x: 0, z: -5 },
    active: false,
  });
  runtime.interactables.push({
    id: idFactory.next("portal"),
    type: "portal",
    name: "Sanctuary Gate",
    position: { x: -8, z: 6 },
    destination: "veilwatch",
  });
  runtime.idCounter = Math.max(runtime.idCounter ?? 0, idFactory.counter);
  return runtime.enemies;
}

export function canUseAbility(
  runtime,
  abilityDef,
  target = null,
  options = {},
) {
  const player = runtime.player;
  if (!player.alive) return { ok: false, reason: "You are defeated." };
  if (runtime.paused || runtime.gameState !== "playing")
    return { ok: false, reason: "The game is paused." };
  if (!abilityDef) return { ok: false, reason: "Unknown ability." };
  if (
    !(CLASSES[runtime.character.classId]?.abilities ?? []).some(
      (entry) => entry.id === abilityDef.id,
    )
  )
    return { ok: false, reason: "Ability does not belong to this class." };
  if ((player.cooldowns[abilityDef.id] ?? 0) > 0)
    return { ok: false, reason: "Ability is cooling down." };
  if (player.resource < abilityDef.cost)
    return {
      ok: false,
      reason: `Not enough ${CLASSES[runtime.character.classId].resource}.`,
    };
  if (hasStatus(player, "stun"))
    return { ok: false, reason: "You are stunned." };
  if (target && (!target.alive || target.faction === "player"))
    return { ok: false, reason: "Invalid target." };
  if (
    target &&
    distance(player.position, target.position) > abilityDef.range + 1
  )
    return { ok: false, reason: "Target is out of range." };
  if (
    target &&
    options.hasLineOfSight &&
    !options.hasLineOfSight(player.position, target.position)
  )
    return { ok: false, reason: "Target is not in line of sight." };
  if (
    abilityDef.summon &&
    runtime.summons.filter((summon) => summon.alive).length >=
      clamp(abilityDef.limit ?? (abilityDef.ultimate ? 5 : 3), 1, 6)
  )
    return { ok: false, reason: "Summon limit reached." };
  if (
    abilityDef.corpse &&
    !runtime.corpses.some(
      (corpse) =>
        !corpse.consumed &&
        distance(player.position, corpse.position) < abilityDef.range,
    )
  )
    return { ok: false, reason: "No fallen remains nearby." };
  return { ok: true };
}

function directionTo(player, targetPosition) {
  const dx = targetPosition.x - player.position.x;
  const dz = targetPosition.z - player.position.z;
  const length = Math.hypot(dx, dz) || 1;
  return { x: dx / length, z: dz / length };
}

function createAbilitySummons(runtime, abilityDef, idFactory, canOccupy) {
  const cap = clamp(abilityDef.limit ?? (abilityDef.ultimate ? 5 : 3), 1, 6);
  const active = runtime.summons.filter((summon) => summon.alive);
  const count = Math.min(abilityDef.ultimate ? 3 : 1, cap - active.length);
  if (count <= 0) return 0;
  for (let index = 0; index < count; index += 1) {
    const angle = (index / Math.max(1, count)) * Math.PI * 2;
    const spawn = {
      x: runtime.player.position.x + Math.cos(angle) * 1.5,
      z: runtime.player.position.z + Math.sin(angle) * 1.5,
    };
    const position =
      !canOccupy || canOccupy(spawn) ? spawn : { ...runtime.player.position };
    runtime.summons.push({
      id: idFactory.next("summon"),
      definitionId: abilityDef.summon ?? "dustbound",
      ownerId: runtime.player.id,
      zoneId: runtime.zoneId,
      position,
      health: 45 + runtime.character.level * 4,
      maxHealth: 45 + runtime.character.level * 4,
      lifetime: abilityDef.ultimate ? 18 : 25,
      cooldown: 0.4,
      state: "follow",
      alive: true,
      generationToken: runtime.tokens?.combat ?? 1,
    });
  }
  runtime.pendingAnnouncements.push("Dustbound allies answer your call.");
  return count;
}

export function executeAbility(
  runtime,
  abilityDef,
  targetPosition,
  target = null,
  rng = new SeededRandom(),
  idFactory = new IdFactory(
    runtime.generationToken,
    runtime.idCounter ?? runtime.actionCounter,
  ),
  options = {},
) {
  const validation = canUseAbility(runtime, abilityDef, target, options);
  if (!validation.ok) return validation;
  const player = runtime.player;
  runtime.actionCounter += 1;
  const attackId = `${runtime.generationToken}:attack:${runtime.actionCounter}`;
  player.resource = clamp(
    player.resource - abilityDef.cost + (abilityDef.generates ?? 0),
    0,
    player.maxResource,
  );
  player.cooldowns[abilityDef.id] = abilityDef.cooldown;
  player.attackRemaining = abilityDef.type === "ultimate" ? 0.9 : 0.35;
  player.animation =
    abilityDef.type === "ultimate"
      ? "victory"
      : abilityDef.type === "channel"
        ? "channel"
        : "cast";
  const direction = directionTo(
    player,
    targetPosition ??
      target?.position ?? { x: player.position.x, z: player.position.z + 1 },
  );
  player.facing = direction;
  const transaction = {
    ok: true,
    attackId,
    abilityId: abilityDef.id,
    hitIds: new Set(),
    effects: [],
  };
  if (abilityDef.corpse) {
    const corpse = runtime.corpses.find(
      (entry) =>
        !entry.consumed &&
        distance(player.position, entry.position) < abilityDef.range,
    );
    if (corpse) corpse.consumed = true;
  }
  runtime.character.statistics.abilityUse[abilityDef.id] =
    (runtime.character.statistics.abilityUse[abilityDef.id] ?? 0) + 1;
  if (abilityDef.healing) {
    const amount = Math.min(
      player.maxHealth - player.health,
      abilityDef.healing + runtime.character.level * 2,
    );
    player.health += amount;
    runtime.character.statistics.healing += amount;
  }
  if (["dash", "teleport"].includes(abilityDef.type)) {
    const length = abilityDef.range;
    const moved = safeMove(
      player.position,
      { x: direction.x * length, z: direction.z * length },
      options.canOccupy,
    );
    player.position.x = clamp(moved.x, -WORLD_SIZE / 2 + 2, WORLD_SIZE / 2 - 2);
    player.position.z = clamp(moved.z, -WORLD_SIZE / 2 + 2, WORLD_SIZE / 2 - 2);
    player.invulnerable = 0.28;
  }
  if (["projectile", "cone"].includes(abilityDef.type)) {
    const projectileCount = abilityDef.projectiles ?? 1;
    for (let index = 0; index < projectileCount; index += 1) {
      const spread = (index - (projectileCount - 1) / 2) * 0.13;
      const cosine = Math.cos(spread),
        sine = Math.sin(spread);
      const rotated = {
        x: direction.x * cosine - direction.z * sine,
        z: direction.x * sine + direction.z * cosine,
      };
      runtime.projectiles.push({
        id: idFactory.next("projectile"),
        attackId,
        ownerId: player.id,
        position: { ...player.position, y: 1 },
        velocity: { x: rotated.x * 16, z: rotated.z * 16 },
        radius: 0.35,
        faction: "player",
        remaining: 1.4,
        damage: abilityDef.damage,
        status: abilityDef.status,
        hitIds: [],
        pierce: abilityDef.pierce ?? 0,
        generationToken: runtime.tokens?.combat ?? 1,
        color: CLASSES[runtime.character.classId].color,
      });
    }
  } else if (
    ["melee", "area", "ultimate"].includes(abilityDef.type) ||
    ["channel", "trap", "dash", "teleport"].includes(abilityDef.type) ||
    abilityDef.radius
  ) {
    const radius = abilityDef.radius ?? abilityDef.range;
    for (let hit = 0; hit < (abilityDef.hits ?? 1); hit += 1) {
      for (const enemy of runtime.enemies) {
        if (!enemy.alive || distance(player.position, enemy.position) > radius)
          continue;
        const hitKey = `${hit}:${enemy.id}`;
        if (transaction.hitIds.has(hitKey)) continue;
        transaction.hitIds.add(hitKey);
        damageEnemy(
          runtime,
          enemy,
          abilityDef.damage,
          abilityDef.status,
          rng,
          `${attackId}:hit:${hit}`,
          idFactory,
        );
      }
    }
    runtime.effects.push({
      id: idFactory.next("effect"),
      type: abilityDef.type,
      position: { ...player.position },
      color: CLASSES[runtime.character.classId].color,
      radius,
      remaining: 0.65,
    });
  } else if (abilityDef.type === "summon") {
    const count = createAbilitySummons(
      runtime,
      abilityDef,
      idFactory,
      options.canOccupy,
    );
    if (count <= 0) return { ok: false, reason: "Summon limit reached." };
  } else if (abilityDef.type === "buff") {
    addStatus(
      player,
      abilityDef.status ?? "fortified",
      player.id,
      runtime.time,
      { amount: 25 },
    );
  }
  if (abilityDef.summon && abilityDef.type !== "summon")
    createAbilitySummons(runtime, abilityDef, idFactory, options.canOccupy);
  runtime.idCounter = Math.max(runtime.idCounter ?? 0, idFactory.counter);
  return transaction;
}

export function damageEnemy(
  runtime,
  enemy,
  amount,
  status,
  rng,
  attackId,
  idFactory = new IdFactory(
    runtime.generationToken,
    runtime.idCounter ?? runtime.actionCounter,
  ),
) {
  enemy.receivedAttackIds ??= [];
  if (!enemy.alive || enemy.receivedAttackIds.includes(attackId)) return null;
  const stats = derivedStats(runtime.character);
  const result = resolveDamage(
    {
      targetAlive: enemy.alive,
      baseDamage: amount,
      attackerModifier: 1 + stats.power / 100,
      abilityModifier: 1,
      elementalModifier: 1,
      criticalChance: stats.crit,
      criticalMultiplier: 1.65,
      damageType: "physical",
      armor: enemy.armor,
      vulnerable: hasStatus(enemy, "vulnerable"),
      weak: hasStatus(runtime.player, "weak"),
      shield: enemy.shield ?? 0,
      status,
    },
    rng,
  );
  enemy.receivedAttackIds.push(attackId);
  enemy.receivedAttackIds = enemy.receivedAttackIds.slice(-64);
  enemy.shield = Math.max(0, (enemy.shield ?? 0) - result.shieldAbsorbed);
  enemy.health = Math.max(0, enemy.health - result.healthDamage);
  runtime.character.statistics.damageDealt += result.healthDamage;
  runtime.character.statistics.highestDamageHit = Math.max(
    runtime.character.statistics.highestDamageHit,
    result.healthDamage,
  );
  if (result.critical) runtime.character.statistics.criticalHits += 1;
  if (status)
    addStatus(enemy, status, runtime.player.id, runtime.time, {
      amount: Math.max(2, amount * 0.12),
    });
  if (enemy.health <= 0) defeatEnemy(runtime, enemy, undefined, idFactory);
  runtime.idCounter = Math.max(runtime.idCounter ?? 0, idFactory.counter);
  return result;
}

export function defeatEnemy(
  runtime,
  enemy,
  rng = new SeededRandom(enemy.lootSeed),
  idFactory = new IdFactory(
    runtime.generationToken,
    runtime.idCounter ?? runtime.actionCounter,
  ),
) {
  if (!enemy.alive) return false;
  enemy.alive = false;
  enemy.state = "dead";
  enemy.health = 0;
  enemy.deathRemaining = 2.2;
  enemy.targetId = null;
  enemy.velocity = { x: 0, z: 0 };
  if (enemy.summonedBy) {
    runtime.idCounter = Math.max(runtime.idCounter ?? 0, idFactory.counter);
    return true;
  }
  runtime.corpses.push({
    id: idFactory.next("corpse"),
    position: { ...enemy.position },
    remaining: 12,
    consumed: false,
  });
  const stats = runtime.character.statistics;
  stats.enemiesDefeated += 1;
  if (enemy.elite) stats.elitesDefeated += 1;
  if (enemy.miniboss) stats.minibossesDefeated += 1;
  if (enemy.bossId) stats.bossesDefeated += 1;
  runtime.character.bestiary[enemy.definitionId] =
    (runtime.character.bestiary[enemy.definitionId] ?? 0) + 1;
  const xp = Math.round(
    (enemy.elite ? 34 : 13) * (enemy.bossId ? 10 : enemy.miniboss ? 4 : 1),
  );
  grantExperience(runtime.character, xp);
  if (rng.next() < (enemy.bossId ? 1 : enemy.elite ? 0.7 : 0.28)) {
    const rarity = enemy.bossId ? "legendary" : null;
    const item = generateItem(
      null,
      runtime.character.level,
      rarity,
      rng,
      idFactory,
    );
    item.position = { ...enemy.position };
    item.owner = "ground";
    runtime.groundItems.push(item);
  }
  if (enemy.bossId && !enemy.challengeBoss) {
    runtime.defeatedBosses ??= [];
    runtime.character.defeatedBosses ??= [];
    if (!runtime.defeatedBosses.includes(enemy.bossId))
      runtime.defeatedBosses.push(enemy.bossId);
    if (!runtime.character.defeatedBosses.includes(enemy.bossId))
      runtime.character.defeatedBosses.push(enemy.bossId);
    runtime.pendingAnnouncements.push(
      `${enemy.name} has fallen. A Crown fragment answers.`,
    );
    progressQuest(runtime.character, "boss", enemy.bossId, 1);
  } else if (!enemy.bossId)
    progressQuest(runtime.character, "defeat", enemy.definitionId, 1);
  if (CLASSES[runtime.character.classId].resource === "Essence")
    runtime.player.resource = Math.min(
      runtime.player.maxResource,
      runtime.player.resource + (enemy.bossId ? 25 : 10),
    );
  runtime.idCounter = Math.max(runtime.idCounter ?? 0, idFactory.counter);
  return true;
}

export function grantExperience(character, amount) {
  character.xp += Math.max(0, Math.floor(amount));
  const levels = [];
  while (character.level < 50) {
    const required = 100 + (character.level - 1) * 55;
    if (character.xp < required) break;
    character.xp -= required;
    character.level += 1;
    character.skillPoints += 1;
    if (character.level % 3 === 0) character.attributePoints += 1;
    levels.push(character.level);
  }
  return levels;
}

export function progressQuest(
  character,
  eventType,
  eventId = null,
  amount = 1,
) {
  const updates = [];
  for (const [questId, state] of Object.entries(character.quests)) {
    if (state.state !== "active") continue;
    const definition = QUESTS.find((entry) => entry.id === questId);
    const objective = definition?.objectives[state.objective];
    if (!objective || objective.type !== eventType) continue;
    if (objective.id && objective.id !== eventId) continue;
    if (objective.faction && objective.faction !== eventId) continue;
    state.progress = Math.min(
      objective.target,
      state.progress + Math.max(0, amount),
    );
    if (state.progress >= objective.target) {
      state.objective += 1;
      state.progress = 0;
      if (state.objective >= definition.objectives.length)
        state.state = "ready";
    }
    updates.push({ questId, state: state.state, objective: state.objective });
  }
  return updates;
}

export function completeQuest(character, questId) {
  const state = character.quests[questId];
  const definition = QUESTS.find((entry) => entry.id === questId);
  if (!state || !definition || state.state !== "ready" || state.rewarded)
    return { ok: false };
  state.state = "completed";
  state.rewarded = true;
  character.currencies.gold += 80 + definition.chapter * 45;
  character.statistics.questsCompleted += 1;
  if (definition.type === "side") character.statistics.sideQuestsCompleted += 1;
  if (questId === "tutorial-awakening")
    unlockAchievement(character, "tutorial");
  if (definition.type === "campaign") {
    character.chapter = Math.min(5, definition.chapter + 1);
    const next = QUESTS.find(
      (entry) => entry.id === `chapter-${character.chapter}`,
    );
    if (next)
      character.quests[next.id] = {
        state: "active",
        objective: 0,
        progress: 0,
      };
    if (questId === "chapter-5") unlockAchievement(character, "campaign");
  }
  return { ok: true, gold: 80 + definition.chapter * 45 };
}

export function unlockAchievement(character, id, diagnostics = false) {
  if (
    diagnostics ||
    !ACHIEVEMENTS.some((entry) => entry.id === id) ||
    character.achievements.includes(id)
  )
    return false;
  character.achievements.push(id);
  return true;
}

export function spendSkillPoint(character, nodeId) {
  const node = (SKILL_TREES[character.classId] ?? []).find(
    (entry) => entry.id === nodeId,
  );
  if (!node || character.skillPoints <= 0)
    return { ok: false, reason: "No skill point available." };
  const rank = character.skills[nodeId] ?? 0;
  if (rank >= node.max) return { ok: false, reason: "Maximum rank reached." };
  if (node.requires && !(character.skills[node.requires] > 0))
    return { ok: false, reason: "Prerequisite not met." };
  character.skills[nodeId] = rank + 1;
  character.skillPoints -= 1;
  return { ok: true, rank: rank + 1 };
}

export function updateEnemyAI(
  runtime,
  enemy,
  dt,
  rng,
  idFactory,
  options = {},
) {
  if (!enemy.alive) return;
  enemy.attackCooldown = Math.max(0, enemy.attackCooldown - dt);
  enemy.thinkRemaining -= dt;
  if (hasStatus(enemy, "stun") || hasStatus(enemy, "freeze")) {
    enemy.state = "stunned";
    enemy.velocity = { x: 0, z: 0 };
    return;
  }
  const toPlayer = distance(enemy.position, runtime.player.position);
  const fromSpawn = distance(
    enemy.position,
    enemy.spawnPosition ?? enemy.position,
  );
  const visible = options.hasLineOfSight
    ? options.hasLineOfSight(enemy.position, runtime.player.position)
    : true;
  if (enemy.thinkRemaining <= 0) {
    enemy.thinkRemaining = 0.12 + rng.next() * 0.18;
    enemy.targetId =
      toPlayer < 15 && fromSpawn < 24 && runtime.player.alive && visible
        ? runtime.player.id
        : null;
    if (!enemy.targetId) enemy.state = "patrol";
    else if (toPlayer <= enemy.range)
      enemy.state = enemy.role === "healer" ? "cast" : "attack";
    else
      enemy.state =
        enemy.role === "ranged" && toPlayer < 4 ? "reposition" : "pursue";
  }
  if (enemy.state === "pursue" || enemy.state === "reposition") {
    const direction = directionTo(enemy, runtime.player.position);
    const sign = enemy.state === "reposition" ? -1 : 1;
    const slow = hasStatus(enemy, "slow") ? 0.55 : 1;
    const charging =
      enemy.role === "charger" &&
      enemy.state === "pursue" &&
      enemy.attackCooldown <= 0 &&
      toPlayer > enemy.range + 1 &&
      toPlayer < 9;
    const movementSpeed = enemy.speed * sign * slow * (charging ? 2.4 : 1);
    enemy.velocity = {
      x: direction.x * movementSpeed,
      z: direction.z * movementSpeed,
    };
    const moved = safeMove(
      enemy.position,
      { x: enemy.velocity.x * dt, z: enemy.velocity.z * dt },
      options.canOccupy,
    );
    enemy.position.x = clamp(moved.x, -WORLD_SIZE / 2 + 1, WORLD_SIZE / 2 - 1);
    enemy.position.z = clamp(moved.z, -WORLD_SIZE / 2 + 1, WORLD_SIZE / 2 - 1);
    if (charging) {
      enemy.attackCooldown = 2.4;
      enemy.state = "pursue";
      runtime.effects.push({
        id: idFactory.next("effect"),
        type: "charge-telegraph",
        position: { ...enemy.position },
        color: "#f0b35f",
        radius: 1.2,
        remaining: 0.45,
      });
    }
  } else enemy.velocity = { x: 0, z: 0 };
  if (
    (enemy.state === "attack" || enemy.state === "cast") &&
    enemy.attackCooldown <= 0 &&
    toPlayer <= enemy.range + 0.6 &&
    visible
  ) {
    if (enemy.role === "summoner") {
      const active = runtime.enemies.filter(
        (entry) => entry.alive && entry.summonedBy === enemy.id,
      );
      if (active.length < 3) {
        const definition = ENEMIES.find(
          (entry) => entry.faction === enemy.faction && entry.role === "melee",
        );
        if (definition) {
          const angle = (enemy.attackCounter ?? 0) * 2.1;
          const desired = {
            x: enemy.position.x + Math.cos(angle) * 1.5,
            z: enemy.position.z + Math.sin(angle) * 1.5,
          };
          const spawnPosition =
            !options.canOccupy || options.canOccupy(desired)
              ? desired
              : { ...enemy.position };
          const summoned = spawnEnemy(
            runtime,
            definition.id,
            spawnPosition,
            { summonedBy: enemy.id },
            idFactory,
          );
          summoned.health = Math.max(1, Math.round(summoned.health * 0.45));
          summoned.maxHealth = summoned.health;
          enemy.attackCooldown = 3.2;
          enemy.state = "summon";
          runtime.effects.push({
            id: idFactory.next("effect"),
            type: "summon-telegraph",
            position: spawnPosition,
            color: "#8edac6",
            radius: 1.4,
            remaining: 0.65,
          });
          return;
        }
      }
    }
    if (["healer", "support"].includes(enemy.role)) {
      const ally = runtime.enemies
        .filter(
          (entry) =>
            entry.alive &&
            entry.id !== enemy.id &&
            entry.faction === enemy.faction &&
            entry.health < entry.maxHealth &&
            distance(enemy.position, entry.position) <= enemy.range + 2,
        )
        .sort((a, b) => a.health / a.maxHealth - b.health / b.maxHealth)[0];
      if (ally) {
        ally.health = Math.min(
          ally.maxHealth,
          ally.health + enemy.damage * 1.4,
        );
        enemy.attackCooldown = 2.2;
        enemy.state = "cast";
        return;
      }
    }
    enemy.attackCooldown = enemy.role === "artillery" ? 2.4 : 1.15;
    enemy.attackCounter = (enemy.attackCounter ?? 0) + 1;
    const incoming = directionTo(runtime.player, enemy.position);
    const facing =
      runtime.player.facing.x * incoming.x +
      runtime.player.facing.z * incoming.z;
    const guarding = runtime.player.blocking && facing >= 0.15;
    const result = resolveDamage(
      {
        targetAlive: runtime.player.alive,
        baseDamage: enemy.damage,
        attackerModifier: 1,
        abilityModifier: 1,
        elementalModifier: 1,
        criticalChance: 0.04,
        damageType: "physical",
        shield: runtime.player.shield ?? 0,
        armor:
          derivedStats(runtime.character).armor +
          (hasStatus(runtime.player, "fortified") ? 18 : 0),
        block: guarding && runtime.player.parryRemaining <= 0,
        parry: guarding && runtime.player.parryRemaining > 0,
        vulnerable: hasStatus(runtime.player, "vulnerable"),
        weak: hasStatus(enemy, "weak"),
      },
      rng,
    );
    if (runtime.player.invulnerable <= 0) {
      runtime.player.shield = Math.max(
        0,
        (runtime.player.shield ?? 0) - result.shieldAbsorbed,
      );
      runtime.player.health = Math.max(
        0,
        runtime.player.health - result.healthDamage,
      );
      runtime.character.statistics.damageReceived += result.healthDamage;
      if (result.blocked) runtime.character.statistics.blocks += 1;
      if (result.parried) runtime.character.statistics.parries += 1;
      const resourceName = CLASSES[runtime.character.classId].resource;
      if (resourceName === "Rage")
        runtime.player.resource = Math.min(
          runtime.player.maxResource,
          runtime.player.resource + Math.max(3, result.healthDamage * 0.35),
        );
      if (resourceName === "Holy Power" && (result.blocked || result.parried))
        runtime.player.resource = Math.min(
          runtime.player.maxResource,
          runtime.player.resource + (result.parried ? 16 : 9),
        );
      const definition = ENEMIES.find(
        (entry) => entry.id === enemy.definitionId,
      );
      if (definition?.status)
        addStatus(runtime.player, definition.status, enemy.id, runtime.time, {
          amount: enemy.damage * 0.1,
        });
    }
    runtime.effects.push({
      id: idFactory.next("effect"),
      type: "enemy-hit",
      position: { ...runtime.player.position },
      color: enemy.elite?.color ?? "#e86065",
      radius: 1.5,
      remaining: 0.35,
    });
  }
}

function updateBossPhase(runtime) {
  const boss = runtime.boss;
  if (!boss?.alive) return [];
  const fraction = boss.health / boss.maxHealth;
  const next = fraction <= 0.33 ? 3 : fraction <= 0.67 ? 2 : 1;
  if (next <= boss.phase) return [];
  const definition = BOSSES.find((entry) => entry.id === boss.bossId);
  const transitions = [];
  for (let phase = boss.phase + 1; phase <= next; phase += 1) {
    if (boss.phaseTransitions.includes(phase)) continue;
    boss.phase = phase;
    boss.phaseTransitions.push(phase);
    boss.damage *= 1.18;
    boss.speed *= 1.08;
    if (runtime.tokens) runtime.tokens.bossPhase += 1;
    transitions.push({
      phase,
      name: definition?.phases[phase - 1] ?? `Phase ${phase}`,
    });
  }
  return transitions;
}

export function fixedUpdate(
  runtime,
  input,
  dt = FIXED_STEP,
  rng = new SeededRandom(runtime.seed),
  idFactory = new IdFactory(runtime.generationToken),
  options = {},
) {
  if (runtime.paused || runtime.gameState !== "playing") return [];
  const events = [];
  runtime.time += dt;
  const player = runtime.player;
  player.invulnerable = Math.max(0, player.invulnerable - dt);
  player.dodgeRemaining = Math.max(0, player.dodgeRemaining - dt);
  player.attackRemaining = Math.max(0, player.attackRemaining - dt);
  player.parryRemaining = Math.max(0, player.parryRemaining - dt);
  const wantsBlock = Boolean(input.block) && player.alive;
  if (wantsBlock && !player.blocking) player.parryRemaining = 0.16;
  player.blocking = wantsBlock;
  for (const id of Object.keys(player.cooldowns))
    player.cooldowns[id] = Math.max(0, player.cooldowns[id] - dt);
  updateStatuses(player, dt, (status) => {
    if (
      ["burn", "poison", "bleed"].includes(status.id) &&
      player.invulnerable <= 0
    ) {
      const amount = status.amount * status.stacks;
      player.health = Math.max(0, player.health - amount);
      runtime.character.statistics.damageReceived += amount;
    }
    if (status.id === "regeneration")
      player.health = Math.min(
        player.maxHealth,
        player.health + status.amount * status.stacks,
      );
  });
  const klass = CLASSES[runtime.character.classId];
  const resourceGeneration = clamp(
    1 + (derivedStats(runtime.character).resourceGeneration ?? 0),
    0,
    2,
  );
  if (klass.resource === "Mana")
    player.resource = Math.min(
      player.maxResource,
      player.resource + 10 * resourceGeneration * dt,
    );
  if (klass.resource === "Energy")
    player.resource = Math.min(
      player.maxResource,
      player.resource + 18 * resourceGeneration * dt,
    );
  if (klass.resource === "Essence")
    player.resource = Math.min(
      player.maxResource,
      player.resource + 4 * resourceGeneration * dt,
    );
  if (klass.resource === "Focus" && Math.hypot(input.x, input.z) > 0.1)
    player.resource = Math.min(
      player.maxResource,
      player.resource + 5 * resourceGeneration * dt,
    );
  if (klass.resource === "Rage" && Math.hypot(input.x, input.z) < 0.1)
    player.resource = Math.max(0, player.resource - 2 * dt);
  if (
    player.alive &&
    !hasStatus(player, "stun") &&
    !hasStatus(player, "freeze") &&
    !hasStatus(player, "root")
  ) {
    const length = Math.hypot(input.x, input.z);
    if (length > 0.01) {
      const stats = derivedStats(runtime.character);
      const speed =
        stats.speed *
        (hasStatus(player, "slow") ? 0.6 : 1) *
        (player.blocking ? 0.45 : 1) *
        (player.dodgeRemaining > 0 ? 2.1 : 1);
      player.velocity = {
        x: (input.x / Math.max(1, length)) * speed,
        z: (input.z / Math.max(1, length)) * speed,
      };
      const next = {
        x: clamp(
          player.position.x + player.velocity.x * dt,
          -WORLD_SIZE / 2 + 1.2,
          WORLD_SIZE / 2 - 1.2,
        ),
        z: clamp(
          player.position.z + player.velocity.z * dt,
          -WORLD_SIZE / 2 + 1.2,
          WORLD_SIZE / 2 - 1.2,
        ),
      };
      const moved = safeMove(
        player.position,
        { x: next.x - player.position.x, z: next.z - player.position.z },
        options.canOccupy,
      );
      player.position.x = moved.x;
      player.position.z = moved.z;
      player.facing = { x: input.x / length, z: input.z / length };
      player.animation =
        player.dodgeRemaining > 0 ? "dodge" : length > 0.8 ? "run" : "walk";
    } else {
      player.velocity = { x: 0, z: 0 };
      player.animation = player.blocking
        ? "block"
        : player.attackRemaining > 0
          ? player.animation
          : "idle";
    }
  }
  const aiStarted = globalThis.performance?.now?.() ?? 0;
  for (const enemy of runtime.enemies.slice()) {
    if (
      enemy.zoneId !== runtime.zoneId ||
      enemy.generationToken !== (runtime.tokens?.zone ?? 1)
    ) {
      enemy.alive = false;
      enemy.deathRemaining = 0;
      continue;
    }
    if (!enemy.alive) {
      enemy.deathRemaining = Math.max(0, (enemy.deathRemaining ?? 0) - dt);
      continue;
    }
    updateStatuses(enemy, dt, (status) => {
      if (["burn", "poison", "bleed"].includes(status.id)) {
        enemy.health = Math.max(
          0,
          enemy.health - status.amount * status.stacks,
        );
        if (enemy.health <= 0) defeatEnemy(runtime, enemy, rng, idFactory);
      }
    });
    updateEnemyAI(runtime, enemy, dt, rng, idFactory, options);
  }
  runtime.diagnostics.aiMs = Math.max(
    0,
    (globalThis.performance?.now?.() ?? aiStarted) - aiStarted,
  );
  runtime.enemies = runtime.enemies.filter(
    (enemy) => enemy.alive || enemy.deathRemaining > 0,
  );
  for (const projectile of runtime.projectiles) {
    if (projectile.generationToken !== (runtime.tokens?.combat ?? 1)) {
      projectile.remaining = 0;
      continue;
    }
    projectile.remaining -= dt;
    const previous = { ...projectile.position };
    projectile.position.x += projectile.velocity.x * dt;
    projectile.position.z += projectile.velocity.z * dt;
    if (options.canOccupy && !options.canOccupy(projectile.position, 0.05)) {
      projectile.remaining = 0;
      continue;
    }
    for (const enemy of runtime.enemies) {
      if (
        !enemy.alive ||
        projectile.hitIds.includes(enemy.id) ||
        segmentDistanceSquared(previous, projectile.position, enemy.position) >
          (projectile.radius + 0.65) ** 2
      )
        continue;
      projectile.hitIds.push(enemy.id);
      damageEnemy(
        runtime,
        enemy,
        projectile.damage,
        projectile.status,
        rng,
        projectile.attackId,
        idFactory,
      );
      if (projectile.hitIds.length > projectile.pierce)
        projectile.remaining = 0;
    }
  }
  runtime.projectiles = runtime.projectiles
    .filter((entry) => entry.remaining > 0)
    .slice(-96);
  for (const effect of runtime.effects) effect.remaining -= dt;
  runtime.effects = runtime.effects
    .filter((entry) => entry.remaining > 0)
    .slice(-128);
  for (const corpse of runtime.corpses) corpse.remaining -= dt;
  runtime.corpses = runtime.corpses
    .filter((entry) => entry.remaining > 0)
    .slice(-20);
  for (const phase of updateBossPhase(runtime))
    events.push({ type: "boss-phase", ...phase });
  if (player.health <= 0 && player.alive) {
    player.alive = false;
    player.animation = "death";
    player.velocity = { x: 0, z: 0 };
    player.path = [];
    runtime.gameState = "dead";
    events.push({ type: "death" });
  }
  return events;
}

export function dodge(runtime, direction) {
  const player = runtime.player;
  if (
    !player.alive ||
    runtime.paused ||
    player.dodgeRemaining > 0 ||
    hasStatus(player, "stun")
  )
    return false;
  player.dodgeRemaining = 0.34;
  player.invulnerable = 0.22;
  player.animation = "dodge";
  if (Math.hypot(direction.x, direction.z) > 0.1) player.facing = direction;
  runtime.character.statistics.dodges += 1;
  return true;
}

export function usePotion(runtime) {
  const player = runtime.player;
  if (!player.alive || player.potions <= 0 || player.health >= player.maxHealth)
    return false;
  player.potions -= 1;
  runtime.character.potions = player.potions;
  const healing = Math.min(
    player.maxHealth - player.health,
    player.maxHealth * 0.42,
  );
  player.health += healing;
  runtime.character.statistics.healing += healing;
  return true;
}

export function pickupItem(runtime, itemId) {
  const index = runtime.groundItems.findIndex((item) => item.id === itemId);
  if (index < 0) return { ok: false, reason: "Loot is no longer available." };
  if (runtime.character.inventory.length >= runtime.character.capacity)
    return {
      ok: false,
      reason: "Inventory full — item remains on the ground.",
    };
  const [item] = runtime.groundItems.splice(index, 1);
  item.owner = "inventory";
  delete item.position;
  runtime.character.inventory.push(item);
  runtime.character.statistics.itemsCollected += 1;
  if (!runtime.character.itemLibrary.includes(item.name))
    runtime.character.itemLibrary.push(item.name);
  if (item.rarity === "legendary") {
    runtime.character.statistics.legendaryItems += 1;
    unlockAchievement(runtime.character, "legendary");
  }
  if (item.rarity === "mythic") {
    runtime.character.statistics.mythicItems += 1;
    unlockAchievement(runtime.character, "mythic");
  }
  progressQuest(runtime.character, "loot", item.id, 1);
  return { ok: true, item };
}

export function activateCheckpoint(runtime, interactable) {
  if (interactable.type !== "checkpoint") return false;
  if (!interactable.active) {
    interactable.active = true;
    runtime.character.checkpoints[runtime.zoneId] = {
      x: interactable.position.x,
      z: interactable.position.z,
    };
    runtime.checkpoint = { ...interactable.position };
    progressQuest(runtime.character, "checkpoint", interactable.id, 1);
  }
  runtime.player.health = Math.min(
    runtime.player.maxHealth,
    runtime.player.health + runtime.player.maxHealth * 0.35,
  );
  return true;
}

export function playerDeath(
  runtime,
  idFactory = new IdFactory(
    runtime.generationToken,
    runtime.idCounter ?? runtime.actionCounter,
  ),
) {
  if (runtime.deathHandledToken === (runtime.tokens?.playerLife ?? 1))
    return runtime.character.echoCache;
  runtime.deathHandledToken = runtime.tokens?.playerLife ?? 1;
  if (runtime.character.echoCache && !runtime.character.echoCache.recovered)
    runtime.character.echoCache.expired = true;
  const lostGold = Math.floor(runtime.character.currencies.gold * 0.12);
  runtime.character.currencies.gold -= lostGold;
  runtime.character.echoCache = {
    id: idFactory.next("echo"),
    zoneId: runtime.zoneId,
    position: { ...runtime.player.position },
    gold: lostGold,
    challengeMarks: 0,
    items: [],
    recovered: false,
    ownerProfile: runtime.character.id,
    createdAt: Date.now(),
  };
  runtime.character.statistics.deaths += 1;
  runtime.player.path = [];
  runtime.player.velocity = { x: 0, z: 0 };
  runtime.idCounter = Math.max(runtime.idCounter ?? 0, idFactory.counter);
  return runtime.character.echoCache;
}

export function respawn(runtime) {
  const point = runtime.checkpoint ?? { x: 0, z: 6 };
  runtime.player.position = { x: point.x, y: 0, z: point.z };
  runtime.player.health = runtime.player.maxHealth;
  runtime.player.statuses = [];
  runtime.player.alive = true;
  runtime.player.animation = "respawn";
  runtime.player.invulnerable = 1.5;
  runtime.player.blocking = false;
  runtime.player.parryRemaining = 0;
  runtime.projectiles = [];
  if (runtime.tokens) {
    runtime.tokens.playerLife += 1;
    runtime.tokens.combat += 1;
  }
  runtime.gameState = "paused";
  runtime.paused = true;
  runtime.character.position = { ...runtime.player.position };
}

export function recoverEcho(runtime) {
  const cache = runtime.character.echoCache;
  if (
    !cache ||
    cache.recovered ||
    cache.expired ||
    cache.zoneId !== runtime.zoneId ||
    distance(runtime.player.position, cache.position) > 2.2
  )
    return false;
  cache.recovered = true;
  runtime.character.currencies.gold += cache.gold;
  runtime.character.currencies.challengeMarks += cache.challengeMarks;
  runtime.character.statistics.echoCachesRecovered += 1;
  return true;
}

export function validateSave(raw) {
  if (
    !raw ||
    typeof raw !== "object" ||
    raw.schemaVersion !== SAVE_SCHEMA_VERSION
  )
    return { valid: false, reason: "Unsupported save schema." };
  const character = raw.character;
  if (!character || !CLASSES[character.classId])
    return { valid: false, reason: "Unknown character class." };
  const name = validateCharacterName(character.name);
  if (!name.valid) return { valid: false, reason: "Invalid character name." };
  for (const key of ["gold", "veilShards", "challengeMarks"])
    if (
      !Number.isFinite(character.currencies?.[key]) ||
      character.currencies[key] < 0
    )
      return { valid: false, reason: "Invalid currency values." };
  if (!REGIONS.some((entry) => entry.id === character.currentZone))
    return { valid: false, reason: "Unknown zone." };
  if (
    !Number.isFinite(character.position?.x) ||
    !Number.isFinite(character.position?.z) ||
    Math.abs(character.position.x) > WORLD_SIZE ||
    Math.abs(character.position.z) > WORLD_SIZE
  )
    return { valid: false, reason: "Invalid player position." };
  const ownership = validateItemOwnership(
    character,
    raw.groundItems ?? [],
    raw.vendorStocks ?? {},
  );
  if (!ownership.valid)
    return { valid: false, reason: "Duplicate or invalid item ownership." };
  const knownQuestIds = new Set(QUESTS.map((entry) => entry.id));
  if (Object.keys(character.quests ?? {}).some((id) => !knownQuestIds.has(id)))
    return { valid: false, reason: "Unknown quest reference." };
  const achievements = character.achievements ?? [];
  const knownAchievementIds = new Set(ACHIEVEMENTS.map((entry) => entry.id));
  if (
    !Array.isArray(achievements) ||
    new Set(achievements).size !== achievements.length ||
    achievements.some((id) => !knownAchievementIds.has(id))
  )
    return { valid: false, reason: "Invalid achievement references." };
  const defeatedBosses = character.defeatedBosses ?? [];
  const knownBossIds = new Set(BOSSES.map((entry) => entry.id));
  if (
    !Array.isArray(defeatedBosses) ||
    new Set(defeatedBosses).size !== defeatedBosses.length ||
    defeatedBosses.some((id) => !knownBossIds.has(id))
  )
    return { valid: false, reason: "Invalid defeated boss references." };
  const validEndings = new Set([
    "Crown Released",
    "Crown Hidden",
    "Crown Reforged",
  ]);
  if (
    !Array.isArray(character.endings ?? []) ||
    new Set(character.endings ?? []).size !==
      (character.endings ?? []).length ||
    (character.endings ?? []).some((ending) => !validEndings.has(ending))
  )
    return { valid: false, reason: "Invalid campaign endings." };
  if (
    Object.values(character.statistics ?? {}).some(
      (value) =>
        typeof value === "number" && (!Number.isFinite(value) || value < 0),
    )
  )
    return { valid: false, reason: "Invalid statistics." };
  const entityIds = [
    raw.player?.id,
    ...(raw.enemies ?? []).map((entry) => entry.id),
    ...(raw.projectiles ?? []).map((entry) => entry.id),
  ].filter(Boolean);
  if (new Set(entityIds).size !== entityIds.length)
    return { valid: false, reason: "Duplicate entity IDs." };
  const itemIds = [
    ...character.inventory,
    ...character.stash,
    ...Object.values(character.equipment).filter(Boolean),
    ...(raw.groundItems ?? []),
    ...Object.values(raw.vendorStocks ?? {}).flat(),
    ...(character.echoCache?.items ?? []),
  ].map((item) => item.id);
  if (
    itemIds.some((id) => typeof id !== "string") ||
    new Set(itemIds).size !== itemIds.length
  )
    return { valid: false, reason: "Duplicate or invalid item IDs." };
  for (const [slot, item] of Object.entries(character.equipment ?? {})) {
    if (!item) continue;
    const expected =
      item.slot === "ring" ? ["ringOne", "ringTwo"] : [item.slot];
    if (
      !expected.includes(slot) ||
      !ITEM_BASES.some(
        (entry) =>
          entry.id === item.definitionId ||
          (entry.slot === item.slot && entry.type === item.type),
      )
    )
      return { valid: false, reason: "Invalid equipped item." };
  }
  for (const [nodeId, rank] of Object.entries(character.skills ?? {})) {
    const node = (SKILL_TREES[character.classId] ?? []).find(
      (entry) => entry.id === nodeId,
    );
    if (!node || !Number.isInteger(rank) || rank < 0 || rank > node.max)
      return { valid: false, reason: "Invalid skill ranks." };
  }
  for (const [questId, state] of Object.entries(character.quests ?? {})) {
    const quest = QUESTS.find((entry) => entry.id === questId);
    if (
      !quest ||
      ![
        "unavailable",
        "available",
        "active",
        "ready",
        "completed",
        "failed",
      ].includes(state.state) ||
      !Number.isInteger(state.objective) ||
      state.objective < 0 ||
      state.objective > quest.objectives.length
    )
      return { valid: false, reason: "Invalid quest state." };
  }
  if (
    (raw.projectiles ?? []).length > 96 ||
    (raw.effects ?? []).length > 128 ||
    (raw.corpses ?? []).length > 20 ||
    (raw.summons ?? []).length > 6
  )
    return { valid: false, reason: "Runtime collections exceed safe limits." };
  return { valid: true, character: { ...character, name: name.name } };
}

export function serializeRuntime(runtime) {
  runtime.character.position = { ...runtime.player.position };
  runtime.character.updatedAt = Date.now();
  runtime.character.playTime += runtime.time - (runtime.lastSavedTime ?? 0);
  runtime.lastSavedTime = runtime.time;
  return clone({
    ...runtime,
    schemaVersion: SAVE_SCHEMA_VERSION,
    paused: true,
    gameState: "paused",
    accumulator: 0,
  });
}

export function hydrateRuntime(raw) {
  const validation = validateSave(raw);
  if (!validation.valid) throw new Error(validation.reason);
  const runtime = clone(raw);
  runtime.paused = true;
  runtime.gameState = "paused";
  runtime.accumulator = 0;
  runtime.player.velocity = { x: 0, z: 0 };
  runtime.tokens = {
    session: runtime.generationToken,
    profile: runtime.character.id,
    zone: 1,
    dungeon: 1,
    combat: 1,
    playerLife: 1,
    bossPhase: 1,
    restore: 1,
    assetLoad: 1,
    pathRequest: 1,
    ...(runtime.tokens ?? {}),
  };
  runtime.tokens.restore += 1;
  runtime.summons ??= [];
  for (const enemy of runtime.enemies ?? []) {
    enemy.zoneId ??= runtime.zoneId;
    enemy.generationToken ??= runtime.tokens.zone;
    enemy.spawnPosition ??= {
      x: enemy.position.x,
      z: enemy.position.z,
    };
    enemy.receivedAttackIds ??= [];
  }
  runtime.player.blocking = false;
  runtime.player.parryRemaining = 0;
  return runtime;
}

export function recruitCompanion(character, companionId) {
  if (
    !COMPANIONS.some((entry) => entry.id === companionId) ||
    character.recruitedCompanions.includes(companionId)
  )
    return false;
  character.recruitedCompanions.push(companionId);
  character.activeCompanion ??= companionId;
  character.statistics.companionsRecruited += 1;
  if (character.recruitedCompanions.length === COMPANIONS.length)
    unlockAchievement(character, "companions");
  return true;
}

export function applyCrafting(
  character,
  operation,
  itemId = null,
  rng = new SeededRandom(),
) {
  const recipes = {
    potion: { gold: 20, shards: 0 },
    key: { gold: 50, shards: 3 },
    dye: { gold: 35, shards: 2 },
    reroll: { gold: 80, shards: 4 },
    improve: { gold: 100, shards: 5 },
  };
  const recipe = recipes[operation];
  if (!recipe) return { ok: false, reason: "Unknown recipe." };
  if (
    character.currencies.gold < recipe.gold ||
    character.currencies.veilShards < recipe.shards
  )
    return { ok: false, reason: "Insufficient crafting materials." };
  const item = itemId
    ? character.inventory.find((entry) => entry.id === itemId)
    : null;
  if (["reroll", "improve"].includes(operation) && !item)
    return { ok: false, reason: "Select an inventory item." };
  if (operation === "potion" && character.potions >= character.potionCapacity)
    return { ok: false, reason: "Potion capacity is already full." };
  if (operation === "improve" && item.level >= 100)
    return { ok: false, reason: "Item is already at maximum level." };
  if (operation === "reroll" && !item.affixes?.length)
    return { ok: false, reason: "Item has no affix to reroll." };
  character.currencies.gold -= recipe.gold;
  character.currencies.veilShards -= recipe.shards;
  if (operation === "potion")
    character.potions = Math.min(
      character.potionCapacity,
      character.potions + 1,
    );
  if (operation === "key")
    character.dungeonKeys = (character.dungeonKeys ?? 0) + 1;
  if (operation === "dye") character.dyes = (character.dyes ?? 0) + 1;
  if (operation === "improve") {
    item.level += 1;
    item.power = (item.power ?? 0) + 2;
    item.armor = (item.armor ?? 0) + 2;
  }
  if (operation === "reroll" && item.affixes.length) {
    const old = item.affixes[item.affixes.length - 1];
    const definition = AFFIXES.find((entry) => entry.id === old.id);
    old.value = Number(
      (definition.min + (definition.max - definition.min) * rng.next()).toFixed(
        old.value < 1 ? 3 : 1,
      ),
    );
  }
  character.statistics.craftingOperations += 1;
  return { ok: true };
}

export const NETWORK_ADAPTER = Object.freeze({
  mode: "offline",
  authority: "local-simulation",
  connect: async () => ({
    connected: false,
    reason: "Realmfall is an offline single-player game.",
  }),
  submitInput: () => false,
  disconnect: () => undefined,
});
